import React from 'react';
import { TrendingUp, TrendingDown, Activity, Coins, BarChart3, CircleDollarSign, Crown, Scale } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';
import { useChart } from '../context/ChartContext';

const QuickStats = () => {
  const { prices, selectedCurrency, currencyConfig } = useCurrency();
  const { theme } = useTheme();
  const { selectMetal, selectedMetal, selectedWeight } = useChart();
  const config = currencyConfig[selectedCurrency];

  const formatPrice = (price) => {
    if (!price) return '-';
    if (price >= 10000) {
      return price.toLocaleString(config.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    return price.toLocaleString(config.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const goldOunce = prices.find(p => p.alias === 'GOLD');
  const goldGram = prices.find(p => p.alias === 'GRAM');
  const silver = prices.find(p => p.alias === 'SILVER');
  const platinum = prices.find(p => p.alias === 'PLAT');
  const goldKilo = prices.find(p => p.alias === 'KILO');
  const elizabeth = prices.find(p => p.alias === 'ELIZ');

  const stats = [
    {
      label: 'Gold Ounce',
      value: formatPrice(goldOunce?.ask),
      change: goldOunce ? ((goldOunce.ask - goldOunce.prevAsk) / goldOunce.prevAsk * 100).toFixed(2) : 0,
      icon: Coins,
      color: '#d4af37',
      chartKey: 'GOLD',
      weight: 'oz'
    },
    {
      label: 'Gold Gram',
      value: formatPrice(goldGram?.ask),
      change: goldGram ? ((goldGram.ask - goldGram.prevAsk) / goldGram.prevAsk * 100).toFixed(2) : 0,
      icon: BarChart3,
      color: '#f59e0b',
      chartKey: 'GOLD',
      weight: 'gr'
    },
    {
      label: 'Gold Kilo',
      value: formatPrice(goldKilo?.ask),
      change: goldKilo ? ((goldKilo.ask - goldKilo.prevAsk) / goldKilo.prevAsk * 100).toFixed(2) : 0,
      icon: Scale,
      color: '#eab308',
      chartKey: 'GOLD',
      weight: 'kilos'
    },
    {
      label: 'Elizabeth Sovereign',
      value: formatPrice(elizabeth?.ask),
      change: elizabeth ? ((elizabeth.ask - elizabeth.prevAsk) / elizabeth.prevAsk * 100).toFixed(2) : 0,
      icon: Crown,
      color: '#fbbf24',
      chartKey: 'ELIZABETH',
      weight: 'oz'
    },
    {
      label: 'Silver Ounce',
      value: formatPrice(silver?.ask),
      change: silver ? ((silver.ask - silver.prevAsk) / silver.prevAsk * 100).toFixed(2) : 0,
      icon: CircleDollarSign,
      color: '#94a3b8',
      chartKey: 'SILVER',
      weight: 'oz'
    },
    {
      label: 'Platinum Ounce',
      value: formatPrice(platinum?.ask),
      change: platinum ? ((platinum.ask - platinum.prevAsk) / platinum.prevAsk * 100).toFixed(2) : 0,
      icon: Activity,
      color: '#60a5fa',
      chartKey: 'PLATINUM',
      weight: 'oz'
    }
  ];

  const isSelected = (stat) => {
    if (stat.chartKey === selectedMetal && stat.weight === selectedWeight) {
      return true;
    }
    // Special case: if it's GOLD and weight matches
    if (stat.chartKey === 'GOLD' && selectedMetal === 'GOLD' && stat.weight === selectedWeight) {
      return true;
    }
    return false;
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {stats.map((stat, index) => (
        <div 
          key={index}
          onClick={() => selectMetal(stat.chartKey, stat.weight)}
          className={`rounded-xl border p-4 transition-all duration-300 cursor-pointer hover:shadow-lg hover:scale-[1.02] ${
            isSelected(stat) ? 'ring-2 ring-offset-2' : ''
          }`}
          style={{ 
            backgroundColor: theme.bgCard, 
            borderColor: isSelected(stat) ? stat.color : theme.border,
            ringColor: stat.color,
            ringOffsetColor: theme.bg
          }}
        >
          <div className="flex items-start justify-between mb-3">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${stat.color}15` }}
            >
              <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
            </div>
            <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
              parseFloat(stat.change) >= 0 
                ? 'bg-green-500/10 text-green-400' 
                : 'bg-red-500/10 text-red-400'
            }`}>
              {parseFloat(stat.change) >= 0 
                ? <TrendingUp className="w-3 h-3" />
                : <TrendingDown className="w-3 h-3" />
              }
              {stat.change > 0 ? '+' : ''}{stat.change}%
            </div>
          </div>
          <p className="text-xs mb-1" style={{ color: theme.textMuted }}>{stat.label}</p>
          <p className="text-xl font-bold font-mono" style={{ color: theme.text }}>
            {config.symbol}{stat.value}
          </p>
        </div>
      ))}
    </div>
  );
};

export default QuickStats;
